import { PropsWithChildren } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { Button } from '@givebutter/primitives/button';
import { DatePicker } from '@givebutter/primitives/date-picker';
import { TimePicker } from '@givebutter/primitives/date-time-picker';
import { getTimeStringFromDateTime } from '@givebutter/util';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ItemFormData, useItemForm } from './useItemForm';
import * as Dialog from 'components/dialog';
import {
    AuctionForm,
    useAuctionSettings,
} from 'components/pages/campaign-settings/campaign-auction/auction-settings/useAuctionSettings';
import { useForceRender } from 'customHooks/useForceRender';
import { DateTime } from 'luxon';

interface ExtendAuctionDialogProps extends PropsWithChildren {
    onExtend?: () => void;
}
const ExtendAuctionDialog = ({ onExtend, children }: ExtendAuctionDialogProps) => {
    const { hasCustomBiddingDateRange } = useItemForm();
    const { setValue, watch } = useFormContext<ItemFormData>();
    const [startDate] = watch('biddingDateRange');
    const { key, forceRender } = useForceRender();
    const { methods, onSubmit } = useAuctionSettings();
    const { control, handleSubmit } = methods;
    const endDate = methods.watch('date.1');

    const {
        field: { onChange, value },
    } = useController({
        name: 'date.1',
        control,
    });

    const onDateChange = (date: DateTime) => {
        const { hour, minute } = endDate;
        const nextValue = date.set({ hour, minute });
        onChange(nextValue);
    };

    const onTimeChange = (time: string) => {
        const { hour, minute } = DateTime.fromFormat(time, 'T');
        const nextValue = endDate.set({ hour, minute });
        onChange(nextValue);
    };

    const onFormSubmit = async (value: AuctionForm) => {
        let date = value.date[1];

        if (hasCustomBiddingDateRange && endDate > DateTime.now()) {
            date = endDate;
        }

        setValue('biddingDateRange', [startDate, date]);
        await onSubmit(value);

        onExtend?.();
        forceRender();
    };

    const getCutoffUntil = () => {
        const now = DateTime.now();
        return now.hasSame(value, 'day')
            ? getTimeStringFromDateTime(
                  now.set({
                      minute: Math.ceil(now.get('minute') / 15) * 15 + 15,
                  }),
              )
            : undefined;
    };

    return (
        <Dialog.Root key={key}>
            {children}
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content>
                    <Dialog.Close />
                    <Dialog.Body>
                        <FontAwesomeIcon
                            className="bg-orange-25 rounded-full p-3 text-2xl text-orange-500"
                            icon={faExclamationTriangle}
                        />
                        <form
                            onSubmit={e => {
                                e.stopPropagation();
                                handleSubmit(onFormSubmit)();
                            }}
                            className="flex flex-col justify-start gap-6"
                        >
                            <div className="flex flex-col gap-2">
                                <h3 className="text-xl font-semibold">Extend item and auction?</h3>
                                <p>
                                    Your auction has ended. To extend this item, set a new end date
                                    and extend the auction.
                                </p>
                            </div>
                            <div className="flex justify-stretch gap-2">
                                <DatePicker
                                    className="flex-1 [&>*]:flex-1"
                                    value={endDate}
                                    onChange={date => onDateChange(date as DateTime)}
                                    minDate={DateTime.now()}
                                />
                                <TimePicker
                                    disabled={value.startOf('day') < DateTime.now().startOf('day')}
                                    cutoffUntil={getCutoffUntil()}
                                    value={getTimeStringFromDateTime(value)}
                                    onValueChange={onTimeChange}
                                />
                            </div>
                            <div className="grid grid-flow-col gap-4">
                                <Dialog.Cancel asChild>
                                    <Button variant="outline" color="neutral">
                                        Cancel
                                    </Button>
                                </Dialog.Cancel>
                                <Button
                                    variant="solid"
                                    type="submit"
                                    disabled={endDate < DateTime.now()}
                                >
                                    Submit
                                </Button>
                            </div>
                        </form>
                    </Dialog.Body>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

const ExtendAuctionDialogTrigger = Dialog.Trigger;

const Root = ExtendAuctionDialog;
const Trigger = ExtendAuctionDialogTrigger;

export { Root, Trigger, ExtendAuctionDialog, ExtendAuctionDialogTrigger };
